import React, { useState, useEffect, useCallback } from 'react';
import { getImageUrl } from '../services/storage';

// Simple in-memory cache for image URLs
const imageUrlCache = new Map();

const FirebaseImage = ({ 
  path, 
  alt = '', 
  className = '', 
  fallbackSrc = '/images/placeholder.jpg',
  cacheKey = null,
  width = null,
  height = null,
  quality = 'medium', // 'low', 'medium', 'high'
  ...props 
}) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [displayWidth, setDisplayWidth] = useState(width);
  const [displayHeight, setDisplayHeight] = useState(height);

  // Parse quality setting to actual dimensions
  useEffect(() => {
    if (quality === 'low') {
      setDisplayWidth(props.width ? Math.min(150, parseInt(props.width)) : 150);
      setDisplayHeight(props.height ? Math.min(200, parseInt(props.height)) : 200);
    } else if (quality === 'medium') {
      setDisplayWidth(props.width ? Math.min(300, parseInt(props.width)) : 300);
      setDisplayHeight(props.height ? Math.min(400, parseInt(props.height)) : 400);
    }
    // For 'high' quality, use original dimensions
  }, [quality, props.width, props.height]);

  const loadImage = useCallback(async () => {
    try {
      setLoading(true);
      
      // Check cache first
      const cacheKeyToUse = cacheKey || path;
      if (imageUrlCache.has(cacheKeyToUse)) {
        setImageUrl(imageUrlCache.get(cacheKeyToUse));
        setLoading(false);
        return;
      }

      const url = await getImageUrl(path);
      if (url) {
        setImageUrl(url);
        // Cache the URL
        imageUrlCache.set(cacheKeyToUse, url);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('Error loading image:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [path, cacheKey]);

  useEffect(() => {
    if (path) {
      loadImage();
    }
  }, [path, loadImage]);

  if (loading) {
    return (
      <div 
        className={`animate-pulse bg-gray-200 ${className}`} 
        style={{ 
          minHeight: displayHeight || props.height || '200px',
          minWidth: displayWidth || props.width || '100%'
        }}
        {...props}
      >
        <div className="w-full h-full" />
      </div>
    );
  }

  if (error || !imageUrl) {
    return (
      <img
        src={fallbackSrc}
        alt={alt}
        className={className}
        width={displayWidth || props.width}
        height={displayHeight || props.height}
        {...props}
      />
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      loading="lazy"
      width={displayWidth || props.width}
      height={displayHeight || props.height}
      {...props}
    />
  );
};

export default FirebaseImage; 